import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { areComponentsEqual } from 'react-hot-loader';
import {
    CSSTransition,
    TransitionGroup,
} from 'react-transition-group';

import { computeHeight } from '../utils/animationUtils';

import styles from './FadeInOut.scss';

const transitionClasses = {
    enter: styles.enter,
    enterActive: styles.enterActive,
    enterDone: styles.enterDone,
    exit: styles.exit,
    exitActive: styles.exitActive,
    exitDone: styles.exitDone,
};

class FadeInOut extends React.Component {
    static propTypes = {
        duration: PropTypes.number.isRequired,
        _className: PropTypes.string,
        className: PropTypes.string,
        setHeight: PropTypes.func,
        show: PropTypes.bool,
    };

    static defaultProps = {
        duration: 300,
        setHeight() {},
    };

    constructor(props) {
        super(props);

        this._childRef = React.createRef();
    }

    _shouldShow() {
        const {
            in: transitionIn,
            show,
        } = this.props;

        if (typeof show !== 'undefined') {
            return show;
        }

        return transitionIn;
    }

    _setHeightAuto = () => {
        this.props.setHeight('auto');
    }

    _setHeight = () => {
        const height = computeHeight(this._childRef);
        this.props.setHeight(height);
    }

    render() {
        const {
            children,
            className,
            _className,
            duration,
            ...transitionProps
        } = this.props;

        return (
            <CSSTransition
                {...transitionProps}
                classNames={transitionClasses}
                in={this._shouldShow()}
                onEntered={this._setHeightAuto}
                onEntering={this._setHeight}
                onExit={this._setHeight}
                onExited={this._setHeightAuto}
                timeout={duration}
            >
                {(transitionState) => {
                    const hasExited = transitionState === 'exited';
                    const hasEntered = transitionState === 'entered';
                    const isEntering = transitionState === 'entering';
                    const transitionStyles = {
                        transitionDuration: `${duration / 2}ms`,
                        transitionDelay: isEntering
                            ? `${duration / 2}ms`
                            : '0ms',
                    };

                    return (
                        <div
                            className={classNames(
                                _className,
                                className,
                                styles.fade,
                                {
                                    [styles.visible]: hasEntered,
                                    [styles.hidden]: hasExited,
                                },
                            )}
                            ref={this._childRef}
                            style={transitionStyles}
                        >
                            {children}
                        </div>
                    );
                }}
            </CSSTransition>
        );
    }
}

class FadeInOutContainer extends React.Component {
    static propTypes = {
        animateHeight: PropTypes.bool,
        className: PropTypes.string,
        childClassName: PropTypes.string,
        duration: PropTypes.number,
    };

    static defaultProps = {
        animateHeight: true,
        className: '',
        childClassName: '',
        duration: 500,
    };

    constructor(props) {
        super(props);

        this.state = {
            height: undefined,
        };
    }

    _setHeight = (height) => {
        if (height !== this.state.height) {
            this.setState(() => ({ height }));
        }
    }

    _augmentChildren(children) {
        const {
            animateHeight,
            duration,
        } = this.props;
        const setHeight = animateHeight
            ? this._setHeight
            : undefined;

        return React.Children.map(children, (child) => {
            if (child && areComponentsEqual(child.type, FadeInOut)) {
                return React.cloneElement(child, {
                    _className: this.props.childClassName,
                    duration: duration,
                    setHeight,
                });
            }

            return child;
        });
    }

    render() {
        const {
            animateHeight,
            className,
            duration,
        } = this.props;
        const { height } = this.state;

        let transitionStyles;
        if (animateHeight) {
            transitionStyles = {
                height,
                transitionDuration: `${duration}ms`,
            };
        }

        return (
            <TransitionGroup
                className={classNames(className, styles.container)}
                style={transitionStyles}
            >
                {this._augmentChildren(this.props.children)}
            </TransitionGroup>
        );
    }
}

export {
    FadeInOut,
    FadeInOutContainer,
};
